import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Fade } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingScreen } from 'shared/components/LoadingScreen';
import {
  LoadingOrchestratorContext,
  type LoadingEntry,
} from './LoadingOrchestratorContext';
import {
  endStartFlow,
  readStartFlow,
  setStartFlowLocale,
  type StartFlowState,
} from './startFlow';
import { StartFlowContext } from './StartFlowContext';
import type { AppLocales } from '@hub/common';

const FADE_TIMEOUT_MS = 250;

export const LoadingOrchestratorProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const [entries, setEntries] = useState<LoadingEntry[]>([]);
  const [flow, setFlow] = useState<StartFlowState | undefined>(readStartFlow);

  const register = useCallback((entry: LoadingEntry) => {
    setEntries(prev => [...prev.filter(e => e.id !== entry.id), entry]);
  }, []);

  const unregister = useCallback((id: string) => {
    setEntries(prev => prev.filter(e => e.id !== id));
  }, []);

  const api = useMemo(() => ({ register, unregister }), [register, unregister]);

  const setFlowLocale = useCallback((locale: AppLocales) => {
    setStartFlowLocale(locale);
    setFlow(prev =>
      prev === undefined || prev.locale !== undefined
        ? prev
        : { ...prev, locale }
    );
  }, []);

  const endFlow = useCallback(() => {
    endStartFlow();
    setFlow(undefined);
  }, []);

  const flowActive = flow !== undefined;
  const startFlowApi = useMemo(
    () => ({ active: flowActive, setLocale: setFlowLocale, end: endFlow }),
    [flowActive, setFlowLocale, endFlow]
  );

  const activeEntry = entries.at(-1); // last-registered wins
  const visible = entries.length > 0;

  const flowT =
    flow === undefined
      ? undefined
      : i18n.getFixedT(flow.locale ?? i18n.language);
  const text = flowT
    ? {
        title: flowT('questionnaireInitializationLoadingTitle'),
        description: flowT('loadingScreenDoNotCloseMessage'),
      }
    : activeEntry;

  return (
    <LoadingOrchestratorContext.Provider value={api}>
      <StartFlowContext.Provider value={startFlowApi}>
        {children}
        <Fade
          in={visible}
          timeout={FADE_TIMEOUT_MS}
          onExited={endFlow}
          unmountOnExit
        >
          <LoadingScreen
            title={text?.title}
            description={text?.description}
            lang={flow?.locale}
            textVisible={flow?.locale !== undefined}
          />
        </Fade>
      </StartFlowContext.Provider>
    </LoadingOrchestratorContext.Provider>
  );
};
